import groq from 'groq';
import { LOCALISE_LANGUAGE_FIELD_NAME, PARENT_FIELD } from '@pkg/config/sanity/constants';
import { DOCUMENT_SLUG_PREFIX } from '@pkg/config/sanity/schemaConfig';

export type PageBySlugPath = {
  parent?: string | null;
  grandParent?: string | null;
  greatGrandParent?: string | null;
  slug?: string;
};

export type BuildPageBySlugQueryOptions = {
  /** GROQ audience predicate (e.g. WWW_AUDIENCE_FILTER / BUSINESS_AUDIENCE_FILTER). */
  audienceFilter: string;
  /** Field projection inside the matched page document. */
  pageFields: string;
};

const typeExclusion = `!(_type in [${Object.keys(DOCUMENT_SLUG_PREFIX).map(type => `"${type}"`).join(', ')}])`;
const localeClause = `${LOCALISE_LANGUAGE_FIELD_NAME} == $locale`;
const slugClause = 'slug.current == $slug';

/**
 * Shared page-by-slug GROQ used by www and business frontends.
 * Audience + field projection stay app-specific; nesting/slug matching is shared.
 *
 * Parent-chain rules: when the URL has no parent segment, the Sanity document must
 * also have no parent. Matching only on `$parent == null` incorrectly includes nested
 * pages that share the same leaf slug (e.g. a hidden acquisitionB2BPages child of a
 * hub page), so `[0]` can return the wrong doc → soft 404 / hydration breakage.
 */
export function buildPageBySlugQuery(
  path: PageBySlugPath | undefined,
  options: BuildPageBySlugQueryOptions,
): string {
  // URL has no parent segment → require unset parent on the document.
  // URL has a parent segment → match that parent slug.
  const parentQuery = path?.parent === null
    ? `!defined(${PARENT_FIELD})`
    : `${PARENT_FIELD}->slug.current == $parent`;
  const grandParentQuery = path?.grandParent === null
    ? `!defined(${PARENT_FIELD}->${PARENT_FIELD})`
    : `${PARENT_FIELD}->${PARENT_FIELD}->slug.current == $grandParent`;
  const greatGrandParentQuery = path?.greatGrandParent === null
    ? `!defined(${PARENT_FIELD}->${PARENT_FIELD}->${PARENT_FIELD})`
    : `${PARENT_FIELD}->${PARENT_FIELD}->${PARENT_FIELD}->slug.current == $greatGrandParent`;

  const pageSelector = groq`"page": *[
    ${typeExclusion} &&
    ${localeClause} &&
    ${slugClause} &&
    ${options.audienceFilter} &&
    ${parentQuery} &&
    ${grandParentQuery} &&
    ${greatGrandParentQuery}
  ][0]`;

  return groq`{
    ${pageSelector} {
      ${options.pageFields}
    }
  }`;
}
